import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL||'',
  headers:{'Authorization':'Bearer '+localStorage.getItem('token')}
});

const today = () => new Date().toISOString().split('T')[0];
const monthStart = () => {
  const d = new Date();
  return d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-01';
};

const COLORS = ['#2563eb','#16a34a','#dc2626','#f59e0b','#7c3aed','#0891b2','#ea580c','#be185d','#065f46','#1e40af'];

export default function TicketCategoryReport() {
  const { tenant } = useAuth();
  const primary = tenant?.branding?.primary_color || '#1B6B3A';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(monthStart());
  const [toDate, setToDate] = useState(today());
  const [expandedCat, setExpandedCat] = useState(null);
  const [activeView, setActiveView] = useState('category');

  const load = async (from, to) => {
    setLoading(true);
    setData(null);
    try {
      const params = {};
      if(from !== undefined ? from : fromDate) params.from_date = from !== undefined ? from : fromDate;
      if(to !== undefined ? to : toDate) params.to_date = to !== undefined ? to : toDate;
      const r = await api.get('/api/reports/ticket-categories', {params});
      setData(r.data);
    } catch(e){ console.error(e); }
    setLoading(false);
  };

  useEffect(()=>{ load(); },[]);

  const subDists = data?.categories ? 
    [...new Set(data.categories.flatMap(c=>Object.keys(c.sub_breakdown||{})))].filter(s=>s&&s!=='Unknown').sort() : [];

  const maxTotal = data?.categories?.[0]?.total || 1;

  const exportPdf = () => {
    if(!data) return;
    const rows = data.categories.map((c,i)=>`
      <tr style="background:${i%2?'#f9fafb':'white'}">
        <td><b>${c.category}</b></td>
        <td style="text-align:right;font-size:15px;font-weight:900;color:#2563eb">${c.total}</td>
        <td style="text-align:right">${data.total?Math.round(c.total/data.total*100):0}%</td>
        <td style="text-align:right;color:#dc2626">${c.open}</td>
        <td style="text-align:right;color:#16a34a">${c.resolved}</td>
        <td style="font-size:11px;color:#64748b">${Object.entries(c.sub_breakdown||{}).sort((a,b)=>b[1]-a[1]).map(([s,n])=>`${s}:${n}`).join(' · ')}</td>
      </tr>`).join('');
    const w = window.open('','_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Ticket Analysis</title>
    <style>
      body{font-family:Arial,sans-serif;margin:0;padding:24px;color:#0f172a}
      .header{background:${primary};color:white;padding:20px 24px;border-radius:12px;margin-bottom:20px}
      .header h1{margin:0;font-size:20px}.header p{margin:4px 0 0;opacity:0.8;font-size:13px}
      .cards{display:flex;gap:12px;margin-bottom:20px;flex-wrap:wrap}
      .card{background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:14px;text-align:center;min-width:110px}
      .card .val{font-size:24px;font-weight:900}.card .lbl{font-size:11px;color:#64748b;margin-top:2px}
      table{width:100%;border-collapse:collapse;font-size:12px}
      th{background:${primary};color:white;padding:10px 12px;text-align:left}
      td{padding:8px 12px;border-bottom:1px solid #f1f5f9}
      .total-row{background:${primary};color:white;font-weight:800}
      h2{color:${primary};margin:20px 0 10px;font-size:15px}
      .others-box{background:#fffbeb;border:1px solid #fde68a;border-radius:10px;padding:16px;margin-top:16px}
      .others-box h3{color:#92400e;margin:0 0 10px;font-size:14px}
      .tag{display:inline-block;background:white;border:1px solid #fde68a;border-radius:6px;padding:4px 10px;margin:3px;font-size:11px}
      @media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact}}
    </style></head><body>
    <div class="header">
      <h1>🎫 Ticket Category Analysis</h1>
      <p>Period: ${fromDate||'All time'} ${toDate?'to '+toDate:''} &nbsp;·&nbsp; Generated: ${new Date().toLocaleString('en-IN')}</p>
    </div>
    <div class="cards">
      <div class="card"><div class="val" style="color:${primary}">${data.total}</div><div class="lbl">Total Tickets</div></div>
      <div class="card"><div class="val" style="color:#7c3aed">${data.categories.length}</div><div class="lbl">Categories</div></div>
      <div class="card"><div class="val" style="color:#dc2626">${data.categories.reduce((a,c)=>a+c.open,0)}</div><div class="lbl">Open</div></div>
      <div class="card"><div class="val" style="color:#16a34a">${data.categories.reduce((a,c)=>a+c.resolved,0)}</div><div class="lbl">Resolved</div></div>
      <div class="card"><div class="val" style="color:#f59e0b">${subDists.length}</div><div class="lbl">Sub-Districts</div></div>
    </div>
    <h2>📊 By Category — Most to Least</h2>
    <table><thead><tr><th>Category</th><th>Total</th><th>Share%</th><th>Open</th><th>Resolved</th><th>Sub-District Breakdown</th></tr></thead>
    <tbody>${rows}
    <tr class="total-row"><td>TOTAL</td><td style="text-align:right">${data.total}</td><td>100%</td>
    <td style="text-align:right">${data.categories.reduce((a,c)=>a+c.open,0)}</td>
    <td style="text-align:right">${data.categories.reduce((a,c)=>a+c.resolved,0)}</td><td></td></tr>
    </tbody></table>
    ${data.others_breakdown?.length>0?`
    <div class="others-box">
      <h3>📋 "Others" Category — What's Inside (${data.categories.find(c=>c.category==='Others')?.total||0} tickets)</h3>
      ${data.others_breakdown.map(o=>`<span class="tag"><b>${o.description}</b> <span style="color:#92400e">${o.count}</span></span>`).join('')}
    </div>`:''}
    </body></html>`);
    w.document.close(); w.print();
  };

  const exportExcel = () => {
    if(!data) return;
    const rows = [['Category','Total','Share%','Open','Resolved','Resolution%',...subDists]];
    data.categories.forEach(c=>{
      rows.push([c.category,c.total,
        (data.total?Math.round(c.total/data.total*100):0)+'%',
        c.open,c.resolved,
        (c.total?Math.round(c.resolved/c.total*100):0)+'%',
        ...subDists.map(s=>c.sub_breakdown?.[s]||0)
      ]);
    });
    rows.push(['TOTAL',data.total,'100%',
      data.categories.reduce((a,c)=>a+c.open,0),
      data.categories.reduce((a,c)=>a+c.resolved,0),'',
      ...subDists.map(s=>data.categories.reduce((a,c)=>a+(c.sub_breakdown?.[s]||0),0))
    ]);
    const csv = rows.map(r=>r.join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
    a.download = 'ticket_analysis_'+(fromDate||'all')+'.csv';
    a.click();
  };

  return (
    <div style={{padding:'24px',maxWidth:'1300px',margin:'0 auto',background:'#f8fafc',minHeight:'100vh'}}>

      {/* Page Header */}
      <div style={{background:'white',borderRadius:'16px',padding:'20px 24px',marginBottom:'20px',
        boxShadow:'0 1px 3px rgba(0,0,0,0.06)',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:'12px'}}>
        <div>
          <h1 style={{margin:0,fontSize:'22px',fontWeight:'900',color:'#0f172a'}}>🎫 Ticket Category Analysis</h1>
          <p style={{margin:'4px 0 0',fontSize:'13px',color:'#64748b'}}>
            Complaint breakdown by category and sub-district — {data?.date_range?.from||'All time'}
            {data?.date_range?.to && data?.date_range?.to!=='All time'?' to '+data.date_range.to:''}
          </p>
        </div>
        <div style={{display:'flex',gap:'8px'}}>
          <button onClick={exportExcel}
            style={{padding:'10px 18px',background:'#16a34a',color:'white',border:'none',borderRadius:'10px',
              cursor:'pointer',fontSize:'13px',fontWeight:'700',display:'flex',alignItems:'center',gap:'6px'}}>
            📥 Excel
          </button>
          <button onClick={exportPdf}
            style={{padding:'10px 18px',background:'#dc2626',color:'white',border:'none',borderRadius:'10px',
              cursor:'pointer',fontSize:'13px',fontWeight:'700',display:'flex',alignItems:'center',gap:'6px'}}>
            🖨️ Print / PDF
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div style={{background:'white',borderRadius:'16px',padding:'16px 20px',marginBottom:'20px',
        boxShadow:'0 1px 3px rgba(0,0,0,0.06)'}}>
        {/* Preset buttons */}
        <div style={{display:'flex',gap:'8px',flexWrap:'wrap',marginBottom:'12px'}}>
          {[
            {l:'📋 All Time', f:()=>{setFromDate('');setToDate('');return ['',''];}},
            {l:'📅 This Month', f:()=>{
              const n=new Date();
              const f=n.getFullYear()+'-'+String(n.getMonth()+1).padStart(2,'0')+'-01';
              const t=n.getFullYear()+'-'+String(n.getMonth()+1).padStart(2,'0')+'-'+String(new Date(n.getFullYear(),n.getMonth()+1,0).getDate()).padStart(2,'0');
              setFromDate(f);setToDate(t);return [f,t];}},
            {l:'📅 Last Month', f:()=>{
              const n=new Date();
              const f=n.getFullYear()+'-'+String(n.getMonth()).padStart(2,'0')+'-01';
              const t=new Date(n.getFullYear(),n.getMonth(),0);
              const ts=t.getFullYear()+'-'+String(t.getMonth()+1).padStart(2,'0')+'-'+String(t.getDate()).padStart(2,'0');
              const fs=new Date(t.getFullYear(),t.getMonth(),1);
              const fss=fs.getFullYear()+'-'+String(fs.getMonth()+1).padStart(2,'0')+'-01';
              setFromDate(fss);setToDate(ts);return [fss,ts];}},
            {l:'📊 Last 3 Months', f:()=>{
              const n=new Date(); const t=today();
              const f=new Date(n.getFullYear(),n.getMonth()-2,1);
              const fs=f.getFullYear()+'-'+String(f.getMonth()+1).padStart(2,'0')+'-01';
              setFromDate(fs);setToDate(t);return [fs,t];}},
            {l:'📈 Last 6 Months', f:()=>{
              const n=new Date(); const t=today();
              const f=new Date(n.getFullYear(),n.getMonth()-5,1);
              const fs=f.getFullYear()+'-'+String(f.getMonth()+1).padStart(2,'0')+'-01';
              setFromDate(fs);setToDate(t);return [fs,t];}},
            {l:'🗓️ This Year', f:()=>{
              const y=new Date().getFullYear();
              const f=y+'-01-01'; const t=today();
              setFromDate(f);setToDate(t);return [f,t];}},
          ].map((btn,i)=>(
            <button key={i} onClick={()=>{ const [f,t]=btn.f(); load(f,t); }}
              style={{padding:'8px 16px',background:'#f8fafc',color:'#475569',
                border:'1px solid #e2e8f0',borderRadius:'10px',cursor:'pointer',
                fontSize:'12px',fontWeight:'700',transition:'all 0.15s'}}
              onMouseEnter={e=>{e.currentTarget.style.background=primary;e.currentTarget.style.color='white';e.currentTarget.style.borderColor=primary;}}
              onMouseLeave={e=>{e.currentTarget.style.background='#f8fafc';e.currentTarget.style.color='#475569';e.currentTarget.style.borderColor='#e2e8f0';}}>
              {btn.l}
            </button>
          ))}
        </div>
        {/* Custom date + view toggle */}
        <div style={{display:'flex',gap:'10px',flexWrap:'wrap',alignItems:'flex-end',paddingTop:'12px',borderTop:'1px solid #f1f5f9'}}>
          <div>
            <div style={{fontSize:'11px',fontWeight:'700',color:'#94a3b8',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:'6px'}}>Custom From</div>
            <input type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)}
              style={{padding:'9px 14px',border:'1px solid #e2e8f0',borderRadius:'10px',fontSize:'13px',outline:'none'}}/>
          </div>
          <div>
            <div style={{fontSize:'11px',fontWeight:'700',color:'#94a3b8',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:'6px'}}>Custom To</div>
            <input type="date" value={toDate} onChange={e=>setToDate(e.target.value)}
              style={{padding:'9px 14px',border:'1px solid #e2e8f0',borderRadius:'10px',fontSize:'13px',outline:'none'}}/>
          </div>
          <button onClick={()=>load(fromDate,toDate)}
            style={{padding:'10px 24px',background:primary,color:'white',border:'none',borderRadius:'10px',
              cursor:'pointer',fontSize:'13px',fontWeight:'700',height:'40px'}}>
            🔍 Generate
          </button>
          <div style={{marginLeft:'auto',display:'flex',gap:'6px'}}>
            {[['category','📊 Category'],['subdist','🏘️ Sub-District']].map(([v,l])=>(
              <button key={v} onClick={()=>setActiveView(v)}
                style={{padding:'10px 16px',border:'2px solid',borderRadius:'10px',cursor:'pointer',fontSize:'12px',fontWeight:'700',
                  borderColor:activeView===v?primary:'#e2e8f0',
                  background:activeView===v?primary:'white',
                  color:activeView===v?'white':'#64748b'}}>
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{background:'white',borderRadius:'16px',padding:'80px',textAlign:'center',boxShadow:'0 1px 3px rgba(0,0,0,0.06)'}}>
          <div style={{fontSize:'32px',marginBottom:'12px'}}>⏳</div>
          <div style={{color:'#94a3b8',fontSize:'14px'}}>Analyzing tickets...</div>
        </div>
      ) : data && (
        <>
          {/* KPI Cards */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:'12px',marginBottom:'20px'}}>
            {[
              {l:'Total Tickets',v:data.total,c:'#2563eb',bg:'#eff6ff',icon:'🎫'},
              {l:'Categories',v:data?.categories?.length||0,c:'#7c3aed',bg:'#f5f3ff',icon:'📂'},
              {l:'Open',v:data?.categories?.reduce((a,c)=>a+c.open,0)||0,c:'#dc2626',bg:'#fef2f2',icon:'🔴'},
              {l:'Resolved',v:data?.categories?.reduce((a,c)=>a+c.resolved,0)||0,c:'#16a34a',bg:'#f0fdf4',icon:'✅'},
              {l:'Sub-Districts',v:subDists.length,c:'#f59e0b',bg:'#fffbeb',icon:'🏘️'},
            ].map(s=>(
              <div key={s.l} style={{background:s.bg,borderRadius:'14px',padding:'18px',border:'1px solid',
                borderColor:s.bg,boxShadow:'0 1px 3px rgba(0,0,0,0.04)'}}>
                <div style={{fontSize:'24px',marginBottom:'8px'}}>{s.icon}</div>
                <div style={{fontSize:'28px',fontWeight:'900',color:s.c,lineHeight:1}}>{s.v?.toLocaleString('en-IN')}</div>
                <div style={{fontSize:'12px',color:'#64748b',marginTop:'6px',fontWeight:'600'}}>{s.l}</div>
              </div>
            ))}
          </div>

          {/* Category View */}
          {activeView==='category' && (
            <div style={{background:'white',borderRadius:'16px',boxShadow:'0 1px 3px rgba(0,0,0,0.06)',overflow:'hidden',marginBottom:'20px'}}>
              <div style={{padding:'16px 20px',borderBottom:'1px solid #f1f5f9',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div style={{fontWeight:'800',fontSize:'15px',color:'#0f172a'}}>📊 Complaints by Category</div>
                <div style={{fontSize:'12px',color:'#94a3b8'}}>{data?.categories?.length||0} categories · Most to least</div>
              </div>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px'}}>
                  <thead>
                    <tr style={{background:'#f8fafc'}}>
                      <th style={{padding:'12px 20px',textAlign:'left',fontWeight:'700',color:'#64748b',fontSize:'11px',textTransform:'uppercase',letterSpacing:'0.5px'}}>#</th>
                      <th style={{padding:'12px 20px',textAlign:'left',fontWeight:'700',color:'#64748b',fontSize:'11px',textTransform:'uppercase',letterSpacing:'0.5px'}}>Category</th>
                      <th style={{padding:'12px 20px',textAlign:'right',fontWeight:'700',color:'#64748b',fontSize:'11px',textTransform:'uppercase',letterSpacing:'0.5px'}}>Total</th>
                      <th style={{padding:'12px 20px',textAlign:'left',fontWeight:'700',color:'#64748b',fontSize:'11px',textTransform:'uppercase',letterSpacing:'0.5px',minWidth:'150px'}}>Share</th>
                      <th style={{padding:'12px 20px',textAlign:'right',fontWeight:'700',color:'#dc2626',fontSize:'11px',textTransform:'uppercase',letterSpacing:'0.5px'}}>Open</th>
                      <th style={{padding:'12px 20px',textAlign:'right',fontWeight:'700',color:'#16a34a',fontSize:'11px',textTransform:'uppercase',letterSpacing:'0.5px'}}>Resolved</th>
                      <th style={{padding:'12px 20px',textAlign:'right',fontWeight:'700',color:'#64748b',fontSize:'11px',textTransform:'uppercase',letterSpacing:'0.5px'}}>Res%</th>
                      <th style={{padding:'12px 20px',textAlign:'center',fontWeight:'700',color:'#64748b',fontSize:'11px',textTransform:'uppercase',letterSpacing:'0.5px'}}>Sub-Dist</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.categories?.map((c,i)=>(
                      <React.Fragment key={i}>
                        <tr style={{borderTop:'1px solid #f1f5f9',transition:'background 0.1s',
                          background:expandedCat===c.category?'#eff6ff':'white'}}
                          onMouseEnter={e=>e.currentTarget.style.background=expandedCat===c.category?'#eff6ff':'#f8fafc'}
                          onMouseLeave={e=>e.currentTarget.style.background=expandedCat===c.category?'#eff6ff':'white'}>
                          <td style={{padding:'14px 20px',color:'#94a3b8',fontWeight:'700'}}>{i+1}</td>
                          <td style={{padding:'14px 20px'}}>
                            <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                              <div style={{width:'10px',height:'10px',borderRadius:'50%',background:COLORS[i%COLORS.length],flexShrink:0}}/>
                              <span style={{fontWeight:'700',color:'#0f172a',fontSize:'13px'}}>{c.category}</span>
                            </div>
                          </td>
                          <td style={{padding:'14px 20px',textAlign:'right',fontWeight:'900',fontSize:'18px',color:COLORS[i%COLORS.length]}}>{c.total}</td>
                          <td style={{padding:'14px 20px'}}>
                            <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                              <div style={{flex:1,height:'6px',background:'#f1f5f9',borderRadius:'3px',overflow:'hidden'}}>
                                <div style={{height:'6px',background:COLORS[i%COLORS.length],borderRadius:'3px',
                                  width:Math.round(c.total/maxTotal*100)+'%',transition:'width 0.5s'}}/>
                              </div>
                              <span style={{fontSize:'12px',fontWeight:'700',color:'#64748b',minWidth:'35px'}}>
                                {data?.total?Math.round(c.total/data.total*100):0}%
                              </span>
                            </div>
                          </td>
                          <td style={{padding:'14px 20px',textAlign:'right',fontWeight:'700',color:'#dc2626'}}>{c.open}</td>
                          <td style={{padding:'14px 20px',textAlign:'right',fontWeight:'700',color:'#16a34a'}}>{c.resolved}</td>
                          <td style={{padding:'14px 20px',textAlign:'right'}}>
                            <span style={{padding:'3px 8px',borderRadius:'20px',fontSize:'11px',fontWeight:'700',
                              background:c.total&&Math.round(c.resolved/c.total*100)>=80?'#f0fdf4':'#fef2f2',
                              color:c.total&&Math.round(c.resolved/c.total*100)>=80?'#16a34a':'#dc2626'}}>
                              {c.total?Math.round(c.resolved/c.total*100):0}%
                            </span>
                          </td>
                          <td style={{padding:'14px 20px',textAlign:'center'}}>
                            <button onClick={()=>setExpandedCat(expandedCat===c.category?null:c.category)}
                              style={{padding:'5px 12px',background:expandedCat===c.category?primary:'#f1f5f9',
                                color:expandedCat===c.category?'white':'#2563eb',border:'none',borderRadius:'8px',
                                cursor:'pointer',fontSize:'11px',fontWeight:'700',transition:'all 0.2s'}}>
                              {expandedCat===c.category?'▲ Hide':'▼ Details'}
                            </button>
                          </td>
                        </tr>
                        {expandedCat===c.category && (
                          <tr style={{background:'#eff6ff'}}>
                            <td colSpan={8} style={{padding:'12px 20px 16px 50px'}}>
                              <div style={{fontSize:'11px',fontWeight:'700',color:'#2563eb',marginBottom:'8px',textTransform:'uppercase',letterSpacing:'0.5px'}}>
                                Sub-District Breakdown
                              </div>
                              <div style={{display:'flex',flexWrap:'wrap',gap:'8px'}}>
                                {Object.entries(c.sub_breakdown||{}).sort((a,b)=>b[1]-a[1]).map(([sub,cnt])=>(
                                  <div key={sub} style={{background:'white',border:'1px solid #bfdbfe',borderRadius:'10px',
                                    padding:'8px 14px',display:'flex',alignItems:'center',gap:'8px'}}>
                                    <span style={{fontWeight:'700',color:'#2563eb',fontSize:'12px'}}>{sub}</span>
                                    <span style={{fontWeight:'900',color:'#0f172a',fontSize:'16px'}}>{cnt}</span>
                                    <span style={{color:'#94a3b8',fontSize:'11px'}}>
                                      ({c.total?Math.round(cnt/c.total*100):0}%)
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                    <tr style={{background:primary}}>
                      <td colSpan={2} style={{padding:'14px 20px',fontWeight:'900',color:'white',fontSize:'14px'}}>TOTAL</td>
                      <td style={{padding:'14px 20px',textAlign:'right',fontWeight:'900',color:'white',fontSize:'20px'}}>{data?.total}</td>
                      <td style={{padding:'14px 20px',color:'rgba(255,255,255,0.7)',fontSize:'12px'}}>100%</td>
                      <td style={{padding:'14px 20px',textAlign:'right',fontWeight:'800',color:'#fca5a5'}}>{data?.categories?.reduce((a,c)=>a+c.open,0)||0}</td>
                      <td style={{padding:'14px 20px',textAlign:'right',fontWeight:'800',color:'#86efac'}}>{data?.categories?.reduce((a,c)=>a+c.resolved,0)||0}</td>
                      <td colSpan={2}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Sub-District View */}
          {activeView==='subdist' && (
            <div style={{background:'white',borderRadius:'16px',boxShadow:'0 1px 3px rgba(0,0,0,0.06)',overflow:'hidden',marginBottom:'20px'}}>
              <div style={{padding:'16px 20px',borderBottom:'1px solid #f1f5f9'}}>
                <div style={{fontWeight:'800',fontSize:'15px',color:'#0f172a'}}>🏘️ Complaints by Sub-District</div>
              </div>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px'}}>
                  <thead>
                    <tr style={{background:'#f8fafc'}}>
                      <th style={{padding:'12px 20px',textAlign:'left',fontWeight:'700',color:'#64748b',fontSize:'11px',textTransform:'uppercase'}}>Sub-District</th>
                      <th style={{padding:'12px 20px',textAlign:'right',fontWeight:'700',color:'#64748b',fontSize:'11px',textTransform:'uppercase'}}>Total</th>
                      <th style={{padding:'12px 20px',textAlign:'left',fontWeight:'700',color:'#64748b',fontSize:'11px',textTransform:'uppercase'}}>Top Complaint Categories</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.sub_summary?.map((s,i)=>(
                      <tr key={i} style={{borderTop:'1px solid #f1f5f9',background:i%2?'#fafafa':'white'}}>
                        <td style={{padding:'14px 20px'}}>
                          <span style={{fontWeight:'800',color:'#2563eb',fontSize:'14px'}}>{s.sub_dist}</span>
                        </td>
                        <td style={{padding:'14px 20px',textAlign:'right',fontWeight:'900',fontSize:'20px',color:'#0f172a'}}>{s.total}</td>
                        <td style={{padding:'14px 20px'}}>
                          <div style={{display:'flex',flexWrap:'wrap',gap:'6px'}}>
                            {Object.entries(s.categories||{}).sort((a,b)=>b[1]-a[1]).slice(0,6).map(([cat,cnt],ci)=>(
                              <span key={cat} style={{background:'#f1f5f9',border:'1px solid #e2e8f0',
                                padding:'4px 10px',borderRadius:'20px',fontSize:'11px',display:'flex',gap:'5px',alignItems:'center'}}>
                                <span style={{width:'6px',height:'6px',borderRadius:'50%',background:COLORS[ci%COLORS.length],flexShrink:0}}/>
                                <span style={{color:'#475569',fontWeight:'600'}}>{cat}</span>
                                <span style={{fontWeight:'900',color:'#0f172a'}}>{cnt}</span>
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Others Breakdown */}
          {data?.others_breakdown?.length>0 && (
            <div style={{background:'white',borderRadius:'16px',boxShadow:'0 1px 3px rgba(0,0,0,0.06)',overflow:'hidden'}}>
              <div style={{padding:'16px 20px',borderBottom:'1px solid #fef3c7',background:'#fffbeb',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div>
                  <div style={{fontWeight:'800',fontSize:'15px',color:'#92400e'}}>📋 Inside "Others" Category</div>
                  <div style={{fontSize:'12px',color:'#78350f',marginTop:'2px'}}>
                    These {data.categories?.find(c=>c.category==='Others')?.total||0} tickets were filed as "Others" — top descriptions shown below
                  </div>
                </div>
                <span style={{background:'#fde68a',color:'#92400e',padding:'4px 12px',borderRadius:'20px',fontSize:'12px',fontWeight:'700'}}>
                  {data.others_breakdown.reduce((a,o)=>a+o.count,0)} tickets
                </span>
              </div>
              <div style={{padding:'16px 20px',display:'flex',flexWrap:'wrap',gap:'8px'}}>
                {data.others_breakdown.map((o,i)=>(
                  <div key={i} style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:'10px',
                    padding:'8px 14px',display:'flex',alignItems:'center',gap:'8px',transition:'all 0.2s'}}
                    onMouseEnter={e=>e.currentTarget.style.background='#fef3c7'}
                    onMouseLeave={e=>e.currentTarget.style.background='#fffbeb'}>
                    <span style={{fontSize:'13px',color:'#78350f',fontWeight:'600',textTransform:'capitalize'}}>{o.description}</span>
                    <span style={{background:'#f59e0b',color:'white',borderRadius:'20px',padding:'2px 8px',fontSize:'12px',fontWeight:'900'}}>{o.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
